/* eslint-disable no-unused-vars */
/* eslint-disable import/no-extraneous-dependencies */
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Box, Button, Flex, Text, useDisclosure } from '@chakra-ui/react';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormValidationToast } from '../../../services/actionToast';
import { InputForm } from '../../inputs/forms/InputForm';
import { FormButton } from '../../buttons/FormButton';
import { ReturnButton } from '../../buttons/ReturnButton';
import { useFetch } from '../../../hooks/useFetch';
import { SwitchInputForm } from '../../inputs/forms/SwitchInputForm';
import AlertModal from '../../AlertModal';
import { PageTitle } from '../../PageTitle';
import { FormBox } from '../../formBox';
import { FormHeading } from '../../formHeading';
import { NumberInputForm } from '../../inputs/forms/NumberInputForm';

interface TopicFormProps {
  defaultValues?: any;
}

interface FormInputsProps {
  name: string;
  value: any;
  placeholder?: string;
  label: string;
  yup: any;
}

export default function TopicForm({ defaultValues = {} }: TopicFormProps) {
  const validationToast = useFormValidationToast();
  const baseUrl = '/v1/admin/rating/topics';
  const { requestApi, error } = useFetch(baseUrl, { actionFeedback: false });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { itemId } = useParams();
  const navigate = useNavigate();

  const yupschema: any = {};
  const defaultValuesSchema: any = {};

  const formInputs: FormInputsProps[] = [
    {
      name: 'name',
      value: defaultValues.name ?? '',
      placeholder: 'Nome do tópico',
      label: 'Nome',
      yup: Yup.string().required('Este campo é obrigatório!'),
    },
    {
      name: 'position',
      value: defaultValues.position ?? 1,
      label: 'Posição',
      yup: Yup.string().required('Este campo é obrigatório!'),
    },
    {
      name: 'status',
      value: defaultValues.status ?? true,
      label: 'Status',
      yup: Yup.boolean(),
    },
  ];

  formInputs.forEach((object) => {
    yupschema[object.name] = object.yup;
    defaultValuesSchema[object.name] = object.value;
  });
  const schema = Yup.object().shape(yupschema);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultValuesSchema,
  });

  const name = formInputs.find((field) => field.name === 'name');
  const position = formInputs.find((field) => field.name === 'position');
  const status = formInputs.find((field) => field.name === 'status');

  const onSubmit = async (values: any) => {
    const params = {
      ...values,
      position: Number(values.position),
    };
    await requestApi(
      defaultValues.name ? 'put' : 'post',
      params,
      defaultValues.name ? `${baseUrl}/${itemId}` : null
    );
    onOpen();
  };

  useEffect(() => {
    if (defaultValues.name) {
      reset(defaultValuesSchema);
    }
  }, [defaultValues]);

  return (
    <FormBox>
      <Flex>
        <ReturnButton previousPage="topics" mb={5} />
        <PageTitle>{defaultValues.name ? 'Editar' : 'Criar'} Tópico</PageTitle>
      </Flex>

      <AlertModal
        alertMsg={
          <Text textAlign="center" fontWeight="bold" fontSize="25px">
            {!error
              ? `Tópico ${defaultValues.name ? 'editado' : 'criado'} com sucesso!`
              : `Erro ao ${defaultValues.name ? 'editar' : 'criar'} o tópico.`}
          </Text>
        }
        msgStatus={error ? 'error' : 'success'}
        closeOnOverlayClick={!!error}
        useSecondaryActions
        isOpenSecondary={isOpen}
        onCloseSecondary={onClose}
        ConfirmButton={
          <Button
            w="250px"
            boxShadow="0px 5px 10px #00000065"
            borderRadius="full"
            onClick={() => navigate('/topics')}
          >
            Retornar para a listagem
          </Button>
        }
      />

      <form noValidate onSubmit={handleSubmit(onSubmit, validationToast)}>
        <FormHeading>Informações Básicas</FormHeading>

        <Flex gap={5}>
          <Box flex={3}>
            <InputForm
              name={name?.name}
              label={name?.label}
              placeholder={name?.placeholder}
              isRequired
              register={register}
              errors={errors}
            />
          </Box>

          <Box flex={3}>
            <NumberInputForm
              name={position?.name}
              label={position?.label}
              isRequired
              control={control}
              errors={errors}
            />
          </Box>

          <Box flex={1}>
            <SwitchInputForm
              name={status?.name}
              label={status?.label}
              errors={errors}
              control={control}
            />
          </Box>
        </Flex>

        <Flex mr="auto">
          <FormButton
            mt={4}
            w="100px"
            isLoading={isSubmitting}
            type="submit"
            textTransform="uppercase"
          >
            Salvar
          </FormButton>
        </Flex>
      </form>
    </FormBox>
  );
}
